import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, cn } from "@nexus/ui";
import type { PreflightReportAsset } from "../../types/preflight.types";
import { mediaKind, mediaKindIcon } from "../../lib/media-type";

export interface ReportAssetPlayerProps {
  asset: PreflightReportAsset;
  label: string;
  /** Re-fetches the report so a load failure (usually an expired signed URL) gets a fresh URL. */
  onReload?: () => void;
  reloadPending?: boolean;
  className?: string;
}

/**
 * Inline 9:16 player shown beside the criteria breakdown, so a reviewer can watch the creative
 * while reading its results (AD-2641). Mounted only while the card is open, so collapsing a card
 * stops its playback; a video with a poster preloads nothing, so "expand all" doesn't start a
 * request per creative.
 */
export function ReportAssetPlayer({
  asset,
  label,
  onReload,
  reloadPending = false,
  className,
}: ReportAssetPlayerProps) {
  const { t } = useTranslation();
  const src = asset.assetUrl ?? undefined;
  // Keyed by URL so a refetch that signs a fresh URL retries instead of staying failed.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = src != null && failedSrc === src;
  const kind = mediaKind(asset.contentType);
  const poster = asset.thumbnailUrl ?? undefined;
  const failedMessage = t(
    "preflight.creativeCard.playbackFailed",
    "Couldn't load this creative.",
  );
  const frameClass = cn(
    "relative mx-auto aspect-[9/16] h-[min(45vh,533px)] overflow-hidden rounded-md lg:h-auto lg:w-[300px]",
    className,
  );

  let media: ReactNode;
  if (!src || kind === "other" || failed) {
    media = (
      <div
        className={cn(
          frameClass,
          "bg-muted text-muted-foreground flex flex-col items-center justify-center gap-2 p-4 text-center text-xs",
        )}
      >
        <Icon name={mediaKindIcon(kind)} size="lg" color="inherit" aria-hidden />
        <span>
          {failed
            ? failedMessage
            : t("preflight.creativeCard.noPreview", {
                name: label,
                defaultValue: "No preview for {{name}}",
              })}
        </span>
        {failed && onReload ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconStart="refresh-cw"
            disabled={reloadPending}
            onClick={onReload}
          >
            {t("preflight.creativeCard.reloadMedia", "Try again")}
          </Button>
        ) : null}
      </div>
    );
  } else if (kind === "image") {
    media = (
      <div className={cn(frameClass, "bg-muted")}>
        <img
          src={src}
          alt={label}
          className="size-full object-contain"
          onError={() => setFailedSrc(src)}
        />
      </div>
    );
  } else {
    media = (
      <div className={cn(frameClass, "bg-black")}>
        <video
          src={src}
          poster={poster}
          controls
          playsInline
          // The poster already fills the frame; without one, metadata is what paints the first frame.
          preload={poster ? "none" : "metadata"}
          aria-label={t("preflight.creativeCard.player", {
            name: label,
            defaultValue: "Play {{name}}",
          })}
          className="size-full object-contain"
          onError={() => setFailedSrc(src)}
        />
      </div>
    );
  }

  // The status region stays mounted across the media swap: a live region inserted together
  // with its text is skipped by some screen readers.
  return (
    <>
      {media}
      <span role="status" className="sr-only">
        {failed ? failedMessage : ""}
      </span>
    </>
  );
}
